import React, { FC } from 'react'

// Constants
import { SKETCH_WEBSITE } from '@sketch/env-config'

// Icons
import { ReactComponent as DocumentUploadIcon } from '@sketch/icons/document-upload-48'
import { ReactComponent as BookOpenIcon } from '@sketch/icons/book-open-48'
import { ReactComponent as PersonsIcon } from '@sketch/icons/persons-2-48'
import { ReactComponent as MacAppDownloadIcon } from '@sketch/icons/mac-app-download-48'
import { ReactComponent as LicenseDiamondIcon } from '@sketch/icons/license-diamond-48'
import { ReactComponent as WorkspaceIcon } from '@sketch/icons/workspace-48'
import { ReactComponent as EnvelopeCheckmarkIcon } from '@sketch/icons/envelope-checkmark-48'
import { ReactComponent as InkPenIcon } from '@sketch/icons/ink-pen-48'
import { ReactComponent as ShieldIcon } from '@sketch/icons/shield-48'
import { ReactComponent as GraduateCapIcon } from '@sketch/icons/graduate-cap-48'

// Components
import {
  Button,
  LinkButton,
  pluralize,
  BannerCardProps,
  useModalContext,
} from '@sketch/components'
import { InviteMembersModal } from 'modules/workspace/modals'

// Styles
import { Icon } from './BannerCardList.styles'
import { useToast } from '@sketch/toasts'

// GQL
import { useResendVerificationEmailMutation } from '@sketch/gql-types'

// Operations
import { WorkspaceSettings } from 'modules/workspace/operations'
import { useAnalytics, routes } from '@sketch/modules-common'

export type BannerType = BannerCardProps

// Constants
const VERIFY_EMAIL_BANNER_ID = 'verify-email'
const DOCUMENTATION_BANNER_ID = 'documentation'
const UPLOAD_DOCUMENTS_BANNER = 'upload-documents'
const DOWNLOAD_LATEST_APP_VERSION_BANNER = 'download-latest-app-version'
const COLLABORATION_BANNER = 'collaboration'
const SUBSCRIBE_BANNER = 'subscribe'
const UPGRADE_PERSONAL_WORKSPACE_BANNER = 'upgrade-personal-workspace'
const ADD_EDITORS_BANNER = 'add-editors'
const MFA_BANNER = 'mfa-banner'
const EDUCATION_BANNER_ID = 'education'

// Types
export type BannerId =
  | typeof VERIFY_EMAIL_BANNER_ID
  | typeof DOCUMENTATION_BANNER_ID
  | typeof UPLOAD_DOCUMENTS_BANNER
  | typeof DOWNLOAD_LATEST_APP_VERSION_BANNER
  | typeof COLLABORATION_BANNER
  | typeof SUBSCRIBE_BANNER
  | typeof UPGRADE_PERSONAL_WORKSPACE_BANNER
  | typeof ADD_EDITORS_BANNER
  | typeof EDUCATION_BANNER_ID

interface VerifyEmailBannerProps extends Pick<BannerType, 'small'> {
  email: string
}

interface AddEditorsBannerProps extends Pick<BannerType, 'small'> {
  workspaceSettings: WorkspaceSettings
}

interface UploadDocumentsBannerProps extends Pick<BannerType, 'small'> {
  showOpenSketchModal: () => void
}

export interface Banner extends BannerType {
  id: BannerId
}

// Handlers
const ResendEmailButton = () => {
  const { showToast } = useToast()
  const { trackEvent } = useAnalytics()

  const [resendVerificationEmail] = useResendVerificationEmailMutation({
    redirectErrors: true,
    onCompleted: () => showToast(`An email has been sent`),
    onError: 'show-toast',
  })

  return (
    <Button
      variant="secondary-untinted"
      size="40"
      onClick={() => {
        trackEvent('ONBOARDING - resend verify email', {
          source: 'verify card',
        })
        resendVerificationEmail()
      }}
    >
      Resend Email
    </Button>
  )
}

interface InviteButtonProps {
  workspaceSettings: WorkspaceSettings
}

const InviteButton: FC<InviteButtonProps> = ({
  workspaceSettings,
  children,
}) => {
  const { showRestrictedModal } = useModalContext()

  const handleShowModal = () => {
    const isOnTrial =
      workspaceSettings.workspace?.customer?.billing?.status === 'TRIALING'
    const plan = workspaceSettings.customer?.subscription?.currentPlan!
    const nextBillingCycleDate = workspaceSettings.customer?.subscription
      .renewalDate!
    const seats = workspaceSettings.workspace?.customer?.billing?.seats!
    const paymentDetails = workspaceSettings.customer?.paymentDetails!
    const isScheduledToBeCancelled = !!workspaceSettings.workspace?.customer
      ?.billing?.subscriptionEnd

    showRestrictedModal(InviteMembersModal, {
      workspaceId: workspaceSettings.workspace?.identifier!,
      customerId: workspaceSettings.customer?.id!,
      isOnTrial,
      nextBillingCycleDate,
      plan,
      seats,
      paymentDetails,
      isScheduledToBeCancelled,
    })
  }

  return (
    <Button variant="secondary-untinted" size="40" onClick={handleShowModal}>
      <>{children}</>
    </Button>
  )
}

// Banners
export const verifyEmailBanner = ({
  email,
  small,
}: VerifyEmailBannerProps) => ({
  id: VERIFY_EMAIL_BANNER_ID as BannerId,
  isDismissable: false,
  small,
  icon: <Icon as={EnvelopeCheckmarkIcon} aria-label="Verify Your Email" />,
  title: 'Verify Your Email',
  description: `Check ${email} and confirm your email`,
  action: <ResendEmailButton />,
})

export const documentationBanner = ({
  small,
}: Pick<BannerType, 'small'> = {}) => ({
  id: DOCUMENTATION_BANNER_ID as BannerId,
  isDismissable: false,
  small,
  icon: <Icon as={BookOpenIcon} aria-label="Get Better at Sketch" />,
  title: 'Get Better at Sketch',
  description:
    'Beginner or expert, here’s everything you need to make the most of Sketch',
  action: (
    <LinkButton
      variant="secondary-untinted"
      size={small ? '40' : '32'}
      to={{ pathname: `${SKETCH_WEBSITE}/docs` }}
      target="_blank"
    >
      View Documentation
    </LinkButton>
  ),
})

export const uploadDocumentsBanner = ({
  small,
  showOpenSketchModal,
}: UploadDocumentsBannerProps) => ({
  id: UPLOAD_DOCUMENTS_BANNER as BannerId,
  isDismissable: false,
  small,
  icon: (
    <Icon
      as={DocumentUploadIcon}
      aria-label="Save a Document to the Workspace"
    />
  ),
  title: 'Save a Document to the Workspace',
  description:
    'Create and save a document in the Mac App to see it in your Workspace',
  action: (
    <Button
      variant="secondary-untinted"
      size={small ? '40' : '32'}
      onClick={showOpenSketchModal}
    >
      Create Document
    </Button>
  ),
})

export const downloadLatestAppBanner = ({
  small,
}: Pick<BannerType, 'small'> = {}) => ({
  id: DOWNLOAD_LATEST_APP_VERSION_BANNER as BannerId,
  isDismissable: false,
  small,
  icon: <Icon as={MacAppDownloadIcon} aria-label="Get the Mac App" />,
  title: 'Get the Mac App',
  description:
    'Download the Mac app to create documents and start collaborating',
  action: (
    <LinkButton
      variant="secondary-untinted"
      size={small ? '40' : '32'}
      to={{
        pathname: `${SKETCH_WEBSITE}/downloads/mac`,
      }}
      target="_blank"
    >
      Download
    </LinkButton>
  ),
})

export const collaborationBanner = ({
  small,
}: Pick<BannerType, 'small'> = {}) => ({
  id: COLLABORATION_BANNER as BannerId,
  isDismissable: false,
  small,
  icon: <Icon as={PersonsIcon} aria-label="Start Collaborating" />,
  title: 'Start Collaborating',
  description: 'Invite people as Editors or Viewers to start collaborating',
  action: (
    <LinkButton
      variant="secondary-untinted"
      size={small ? '40' : '32'}
      to={routes.WORKSPACE_AGNOSTIC_SETTINGS_PEOPLE.create({})}
    >
      Invite People
    </LinkButton>
  ),
})

export const subscribeBanner = ({ small }: Pick<BannerType, 'small'> = {}) => ({
  id: SUBSCRIBE_BANNER as BannerId,
  isDismissable: false,
  small,
  icon: <Icon as={LicenseDiamondIcon} aria-label="Subscribe to Sketch" />,
  title: 'Subscribe to Sketch',
  description: 'Subscribe to keep using Sketch after the trial ends',
  action: (
    <LinkButton
      variant="secondary-untinted"
      size={small ? '40' : '32'}
      to={routes.WORKSPACE_AGNOSTIC_SETTINGS_BILLING.create({})}
    >
      Set Up Subscription
    </LinkButton>
  ),
})

export const upgradePersonalWorkspaceBanner = ({
  small,
}: Pick<BannerType, 'small'> = {}) => {
  return {
    id: UPGRADE_PERSONAL_WORKSPACE_BANNER as BannerId,
    isDismissable: true,
    small,
    icon: (
      <Icon as={WorkspaceIcon} aria-label="Upgrade Your Legacy Workspace" />
    ),
    title: 'Upgrade Your Legacy Workspace',
    description: 'Switch to a subscription, starting with a free trial',
    action: (
      <LinkButton
        variant="secondary-untinted"
        size={small ? '40' : '32'}
        to={routes.WORKSPACE_AGNOSTIC_SETTINGS_PEOPLE.create({})}
      >
        Start Upgrade
      </LinkButton>
    ),
  }
}

export const addEditorsBanner = ({
  workspaceSettings,
  small,
}: AddEditorsBannerProps) => {
  const availableSeats =
    workspaceSettings?.workspace?.customer?.billing?.seats.availableSeats || 0

  const editorsCopy = pluralize('Editor', 'Editors', availableSeats)
  const title = `Ready to Add ${availableSeats} ${editorsCopy}`
  const description = pluralize(
    'Invite somebody to edit Sketch documents',
    'Invite people to edit Sketch documents',
    availableSeats
  )

  return {
    id: ADD_EDITORS_BANNER as BannerId,
    isDismissable: true,
    small,
    icon: <Icon as={InkPenIcon} aria-label={title} />,
    title,
    description,
    action: (
      <InviteButton workspaceSettings={workspaceSettings}>
        Invite {editorsCopy}
      </InviteButton>
    ),
  }
}

export const mfaBanner = ({ small }: Pick<BannerType, 'small'> = {}) => {
  const title = `Protect Your Account`
  const description =
    'Use two-factor authentication (2FA) when you sign in to better protect your account'

  return {
    id: MFA_BANNER as BannerId,
    isDismissable: true,
    small,
    icon: <Icon as={ShieldIcon} aria-label={title} />,
    title,
    description,
    action: (
      <LinkButton
        variant="secondary-untinted"
        size={small ? '40' : '32'}
        to={routes.PERSONAL_SETTINGS.create({})}
      >
        Manage Account
      </LinkButton>
    ),
  }
}

const EducationLink = ({ small }: Pick<BannerType, 'small'> = {}) => (
  <LinkButton
    variant="secondary-untinted"
    size={small ? '40' : '32'}
    to={{ pathname: `${SKETCH_WEBSITE}/store/education` }}
    target="_blank"
  >
    Send Your Details
  </LinkButton>
)

export const educationBanner = ({ small }: Pick<BannerType, 'small'> = {}) => {
  const title = 'Get a Free Workspace'
  const description =
    'Sketch for Education offers free Workspaces to students and teachers'

  return {
    id: EDUCATION_BANNER_ID as BannerId,
    isDismissable: true,
    small,
    icon: <Icon as={GraduateCapIcon} aria-label={title} />,
    title,
    description,
    action: <EducationLink small={small} />,
  }
}
